<template>
    <div>
        <ViewSelector
            :is-dirty="intermediateDirty"
            :default-view="defaultView"
            @view-changed="handleViewChange"
            @view-updated="handleViewUpdated"
            @loaded="handleViewsLoaded"
            :items="[]"
        />
    </div>
</template>

<script lang="ts" setup>

import ViewSelector from './ViewSelector.vue';
import type PagePreference from '@agyletime/vue-3-common/src/typings/pagePreference';
import type { InjectedPagePreferences } from '@agyletime/vue-3-common/src/typings/pagePreference';
import { useIntermediateDirty } from '@agyletime/vue-3-common/src/composables/useIntermediateDirty';
import { toRawDeep } from '@agyletime/vue-3-common/src/utils/toRawDeep';
import { Toast } from '@agyletime/vue-3-common/src/components/Toast';
import { useViewFromURL } from '@agyletime/vue-3-common/src/composables/useViewFromURL';
import { updateUrlParams } from '@/components/TeamAndSchedule/utils/urlParamsUtils';

import {
    defineEmits,
    inject,
    ref,
    watch
} from 'vue';

const emits = defineEmits([
    'viewUpdated', 'viewChanged', 'viewLoaded'
]);

const defaultView = ref<PagePreference | null>(null);
const pagePreferences = inject<InjectedPagePreferences>('pagePreferences');
const intermediateDirty = useIntermediateDirty();

function handleViewChange(
    view: PagePreference | null,
    keepIntermediate = false
) {
    if (pagePreferences) {
        if (view)
        {
            pagePreferences.selected.value = toRawDeep(JSON.parse(JSON.stringify(view)));
        }
        else
        {
            pagePreferences.selected.value = toRawDeep(JSON.parse(JSON.stringify({ exit: true })));
        }

        /**
         * keepIntermediate would be true on first load.
         * All other cases it'll be false to allow the view to overrite
         * the current changes under the assumption the user has been
         * warned.
         */
        if (keepIntermediate) {
            pagePreferences.addIntermediate({
                ...toRawDeep(view ?? {}),
                ...pagePreferences.intermediate.value
            }, null, 'handleViewChange');
        } else {
            pagePreferences.addIntermediate(view ? toRawDeep(view) : { isExit: true }, null, 'handleViewChange');
        }

        emits('viewChanged', pagePreferences.selected.value);
    }
}

function handleViewUpdated(view: PagePreference) {
    emits('viewUpdated', view);
}

function handleViewsLoaded() {
    /** TODO: Implement this method if needed */
}

function loadViewFromUrl() {
    // Try and load a view from the url (`view.selected=...`)
    const viewIdFromURL = useViewFromURL();
    if (viewIdFromURL) {
        const view = pagePreferences?.preferences.value?.find(
            (pref) => pref._id === viewIdFromURL
        );
        if (view) {
            defaultView.value = view;
            emits('viewLoaded', view);
            Toast.addToast('loadedViewFromUrl', 'Successfully loaded view from the URL.', 'SUCCESS');
            return;
        }
    }

    // If we didn't load anything from the URL,
    // then we'll try and use the favourited default view.
    loadViewFromFavourite();

    emits('viewLoaded');
}

function loadViewFromFavourite() {
    const favouriteView = pagePreferences?.preferences.value?.find(
        (pref) => pref.isFavourite
    );
    if (favouriteView) {
        defaultView.value = favouriteView;
        Toast.addToast('defaultViewLoaded', 'Successfully loaded default view.', 'SUCCESS');
    }
}

watch(() => pagePreferences?.preferences.value, loadViewFromUrl, { once: true });

function getViewIds(preferences: PagePreference[]) {
    return preferences.map((pref) => pref._id);
}

function getDeletedViewIds(newIds: string[], oldIds: string[]) {
    return oldIds.filter((id) => !newIds.includes(id));
}

function clearSelectedView() {
    defaultView.value = null;
    if (pagePreferences?.selected?.value) {
        pagePreferences.selected.value = null;
    }
    emits('viewChanged', null);
}

function handleDeletedViews(newPreferences: PagePreference[], oldPreferences: PagePreference[]) {
    const newViewIds = getViewIds(newPreferences);
    const oldViewIds = getViewIds(oldPreferences);
    const deletedViewIds = getDeletedViewIds(newViewIds, oldViewIds);
    
    if (deletedViewIds.length === 0) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const selectedViewId = urlParams.get('view.selected');
    
    if (selectedViewId && deletedViewIds.includes(selectedViewId)) {
        updateUrlParams({ 'view.selected': undefined });
        clearSelectedView();
    }
}

watch(
    () => pagePreferences?.preferences.value,
    (newPreferences, oldPreferences) => {
        if (!newPreferences || !oldPreferences) return;
        handleDeletedViews(newPreferences, oldPreferences);
    },
    { deep: true }
);
</script>
